import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Search, Users, ArrowUpDown, ArrowUp, ArrowDown, Pencil, Save, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmt = (val) => { const n = parseFloat(val || 0); return n % 1 === 0 ? String(n) : n.toFixed(1); };

const ShiftProAttendance = ({ user }) => {
  const [data, setData] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [missedPunchMap, setMissedPunchMap] = useState({});
  const [todayStatus, setTodayStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [editMode, setEditMode] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyEdits, setDailyEdits] = useState({});
  const [cachedInfo, setCachedInfo] = useState(null);
  const isHR = user?.role === 'HR';
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${last}`;
  });

  useEffect(() => { fetchOutlets(); }, []);
  useEffect(() => { if (fromDate && toDate) fetchData(); }, [fromDate, toDate, selectedOutlet]);

  const fetchOutlets = async () => {
    try { const res = await api.get('/shiftpro/outlets'); setOutlets(res.data || []); } catch { /* ignore */ }
  };

  const fetchData = async () => {
    setLoading(true);
    setCachedInfo(null);
    try {
      let url = `/shiftpro/monthly-summary?from_date=${fromDate}&to_date=${toDate}`;
      if (selectedOutlet) url += `&outlet=${selectedOutlet}`;
      const [attRes, mpRes, todayRes] = await Promise.allSettled([
        api.get(url),
        api.get('/shiftpro/approvals/missed-punch/all'),
        api.get('/shiftpro/attendance-today'),
      ]);
      const attData = attRes.status === 'fulfilled' ? attRes.value.data : {};
      const rows = attData?.rows || attData || [];
      setData(Array.isArray(rows) ? rows : []);
      if (attData?._cached) {
        const errType = attData._error || '';
        const errMsg = errType === 'auth_failed' ? 'ShiftPro authentication failed. Check credentials.' :
          errType === 'connection_error' ? 'ShiftPro server is unreachable.' :
          'ShiftPro data temporarily unavailable.';
        setCachedInfo({ time: attData._cache_time || 'Unknown', error: errMsg });
        toast.warning(errMsg + ' Showing cached data.');
      } else {
        setCachedInfo(null);
      }
      const mpData = mpRes.status === 'fulfilled' && Array.isArray(mpRes.value.data) ? mpRes.value.data : [];
      const mpMap = {};
      mpData.forEach(mp => {
        const mpDate = mp.date || '';
        if (mpDate >= fromDate && mpDate <= toDate && mp.employee_code) {
          mpMap[mp.employee_code] = (mpMap[mp.employee_code] || 0) + 1;
        }
      });
      setMissedPunchMap(mpMap);
      const todayData = todayRes.status === 'fulfilled' && Array.isArray(todayRes.value.data) ? todayRes.value.data : [];
      const tMap = {};
      todayData.forEach(t => {
        if (t.employee_code) {
          tMap[t.employee_code] = { punch_in: t.punch_in, punch_out: t.punch_out };
        }
      });
      setTodayStatus(tMap);
    } catch (error) {
      toast.error('Failed to fetch ShiftPro data');
      setData([]);
      setMissedPunchMap({});
    } finally { setLoading(false); }
  };

  const updateEdit = (empCode, field, value) => {
    setEdits(prev => ({
      ...prev,
      [empCode]: { ...(prev[empCode] || {}), [field]: parseFloat(value) || 0 }
    }));
  };

  const getVal = (r, field) => {
    const empCode = r.employee_code;
    if (editMode && edits[empCode] && edits[empCode][field] !== undefined) return edits[empCode][field];
    return r[field] || 0;
  };

  const handleSaveEdits = async () => {
    const period = `${fromDate}_${toDate}`;
    const entries = Object.entries(edits).filter(([_, fields]) => Object.keys(fields).length > 0);
    if (entries.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      for (const [empCode, fields] of entries) {
        await api.post('/shiftpro/attendance-override', { period, employee_code: empCode, fields });
      }
      toast.success(`Saved edits for ${entries.length} employee(s)`);
      setEditMode(false);
      setEdits({});
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const enterEditMode = () => {
    const initial = {};
    data.forEach(r => {
      initial[r.employee_code] = {
        reg_p: r.reg_p || 0, weekly_off: r.weekly_off || 0, ot_hours: r.ot_hours || 0,
        odw: r.odw || 0, half_days: r.half_days || 0, leaves: r.leaves || 0,
        absent: r.absent || 0, od: r.od || 0, late_minutes: r.late_minutes || 0,
      };
    });
    setEdits(initial);
    setEditMode(true);
  };

  const toggleExpand = async (empCode, empName) => {
    if (expandedEmp === empCode) { setExpandedEmp(null); setDailyData([]); setDailyEdits({}); return; }
    setExpandedEmp(empCode);
    setDailyLoading(true);
    setDailyEdits({});
    try {
      const res = await api.get(`/shiftpro/employee-daily-attendance?employee_code=${empCode}&name=${encodeURIComponent(empName)}&from_date=${fromDate}&to_date=${toDate}`);
      setDailyData(res.data || []);
    } catch { toast.error('Failed to load daily data'); setDailyData([]); }
    finally { setDailyLoading(false); }
  };

  const updateDailyEdit = (date, field, value) => {
    setDailyEdits(prev => ({
      ...prev,
      [date]: { ...(prev[date] || {}), [field]: field === 'status' ? value : (parseFloat(value) || 0) }
    }));
  };

  const saveDailyEdits = async () => {
    if (Object.keys(dailyEdits).length === 0) { toast.info('No daily changes'); return; }
    setSaving(true);
    try {
      const empRow = data.find(r => r.employee_code === expandedEmp);
      const empName = empRow?.name || '';
      const result = await api.post('/shiftpro/daily-attendance-override', {
        period: `${fromDate}_${toDate}`,
        employee_code: expandedEmp,
        name: empName,
        days: dailyEdits,
      });
      toast.success('Daily edits saved & summary updated');
      setDailyEdits({});
      
      // Update parent summary row with recalculated counts (only if recalculation succeeded)
      if (result.data?.recalculated && Object.keys(result.data.recalculated).length > 0) {
        const rc = result.data.recalculated;
        setData(prev => prev.map(r => {
          if (r.employee_code === expandedEmp) {
            return { ...r, ...rc, _mps_edited: true };
          }
          return r;
        }));
      }
      
      // Reload daily data
      const res = await api.get(`/shiftpro/employee-daily-attendance?employee_code=${expandedEmp}&name=${encodeURIComponent(empName)}&from_date=${fromDate}&to_date=${toDate}`);
      setDailyData(res.data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const calcPresent = (r) => (r.reg_p || 0) + (r.odw || 0);

  const calcSalaryDays = (r) => (r.salary_days != null && !r._mps_edited) ? r.salary_days : (r.reg_p || 0) + (r.weekly_off || 0) + (r.odw || 0) - 0.5 * (r.half_days || 0) + (r.leaves_approved || 0);

  const calcPLRemaining = (r) => r.pl_balance || 0;

  const formatPLBal = (r) => fmt(r.pl_balance || 0);

  const formatDeductDays = (r) => {
    const leaves = r.leaves || 0;
    const absent = r.absent || 0;
    const plApproved = r.leaves_approved || 0;
    const totalAbsent = leaves + absent;
    if (totalAbsent === 0) return null;
    const uncovered = totalAbsent - plApproved;
    if (uncovered <= 0) return 'PL Adjusted';
    if (plApproved > 0) return `${fmt(uncovered)} (PL: ${fmt(plApproved)})`;
    return fmt(uncovered);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="h-3 w-3 opacity-30 ml-0.5 inline" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-yellow-300 ml-0.5 inline" />
      : <ArrowDown className="h-3 w-3 text-yellow-300 ml-0.5 inline" />;
  };

  const filtered = data.filter(r => {
    if (search) {
      const s = search.toLowerCase();
      if (!(r.name || '').toLowerCase().includes(s) && !(r.employee_code || '').toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      let va, vb;
      if (sortKey === 'name') {
        va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortKey === 'employee_code') {
        va = a.employee_code || ''; vb = b.employee_code || '';
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortKey === 'outlet') {
        va = (a.outlet || '').toLowerCase(); vb = (b.outlet || '').toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortKey === 'missed_punch') {
        va = missedPunchMap[a.employee_code] || 0; vb = missedPunchMap[b.employee_code] || 0;
      } else if (sortKey === 'salary_days') {
        va = calcSalaryDays(a); vb = calcSalaryDays(b);
      } else if (sortKey === 'pl_remaining') {
        va = calcPLRemaining(a); vb = calcPLRemaining(b);
      } else {
        va = parseFloat(a[sortKey] || 0); vb = parseFloat(b[sortKey] || 0);
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [filtered, sortKey, sortDir, missedPunchMap]);

  const totals = sorted.reduce((acc, r) => ({
    reg_p: acc.reg_p + calcPresent(r),
    weekly_off: acc.weekly_off + (r.weekly_off || 0),
    ot_hours: acc.ot_hours + (r.ot_hours || 0),
    odw: acc.odw + (r.odw || 0),
    od: acc.od + (r.od || 0),
    leaves: acc.leaves + (r.leaves || 0),
    absent: acc.absent + (r.absent || 0),
    half_days: acc.half_days + (r.half_days || 0),
    late_minutes: acc.late_minutes + (r.late_minutes || 0),
    missed_punch: acc.missed_punch + (missedPunchMap[r.employee_code] || 0),
    pl_remaining: acc.pl_remaining + calcPLRemaining(r),
    salary_days: acc.salary_days + calcSalaryDays(r),
  }), { reg_p: 0, weekly_off: 0, ot_hours: 0, odw: 0, od: 0, leaves: 0, absent: 0, half_days: 0, late_minutes: 0, missed_punch: 0, pl_remaining: 0, salary_days: 0 });

  const d = <span className="text-slate-300">-</span>;

  const thCls = "px-2 py-2 text-right font-medium cursor-pointer select-none hover:text-yellow-300 transition-colors whitespace-nowrap";

  return (
    <div className="space-y-5" data-testid="shiftpro-attendance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">ShiftPro Attendance</h1>
          <p className="mt-1 text-base text-slate-600">Monthly attendance report from ShiftPro</p>
        </div>
        <div className="flex items-center gap-3">
          {isHR && !editMode && (
            <Button onClick={enterEditMode} variant="outline" size="sm" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50" data-testid="edit-attendance-btn">
              <Pencil className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          )}
          {editMode && (
            <>
              <Button onClick={handleSaveEdits} disabled={saving} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-attendance-btn">
                <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button onClick={() => { setEditMode(false); setEdits({}); }} variant="outline" size="sm" data-testid="cancel-edit-btn">
                <X className="h-4 w-4 mr-1.5" /> Cancel
              </Button>
            </>
          )}
          <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5">
            <Users className="h-4 w-4 text-violet-600" />
            <span className="text-sm font-semibold text-violet-700">{sorted.length} Employees</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div>
          <Label>From Date</Label>
          <div className="flex items-center gap-2 mt-1">
            <Calendar className="h-4 w-4 text-slate-500" />
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-44" data-testid="sp-from-date" />
          </div>
        </div>
        <div>
          <Label>To Date</Label>
          <div className="flex items-center gap-2 mt-1">
            <Calendar className="h-4 w-4 text-slate-500" />
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-44" data-testid="sp-to-date" />
          </div>
        </div>
        <div className="min-w-[180px]">
          <Label>Outlet</Label>
          <select value={selectedOutlet} onChange={e => setSelectedOutlet(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="sp-outlet-filter">
            <option value="">All Outlets</option>
            {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="min-w-[200px]">
          <Label>Search</Label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name or Code..." className="pl-9" data-testid="sp-search" />
          </div>
        </div>
      </div>

      {cachedInfo && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 mb-3 flex items-center gap-2" data-testid="cached-warning">
          <svg className="w-5 h-5 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" /></svg>
          <span className="text-sm text-amber-800">{cachedInfo.error || 'ShiftPro is currently unavailable.'} Showing cached data from <strong>{new Date(cachedInfo.time).toLocaleString()}</strong>.</span>
          <button onClick={fetchData} className="ml-auto text-sm font-medium text-amber-700 hover:text-amber-900 underline">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <p className="text-slate-500">No attendance data found for this period.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="sp-table">
          <div className="overflow-auto max-h-[calc(100vh-280px)]">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-20">
                <tr className="bg-slate-800 text-white">
                  <th className="px-2 py-2.5 text-center font-semibold sticky top-0 z-20 bg-slate-800" rowSpan={2}>Sr</th>
                  <th className="px-2 py-2.5 text-center font-semibold sticky top-0 z-20 bg-slate-800 cursor-pointer select-none hover:text-yellow-300" rowSpan={2} onClick={() => handleSort('employee_code')}>Code <SortIcon col="employee_code" /></th>
                  <th className="px-3 py-2.5 text-left font-semibold sticky left-0 top-0 z-30 bg-slate-800 min-w-[150px] cursor-pointer select-none hover:text-yellow-300" rowSpan={2} onClick={() => handleSort('name')}>Employee <SortIcon col="name" /></th>
                  <th className="px-2 py-2.5 text-center font-semibold sticky top-0 z-20 bg-slate-800 cursor-pointer select-none hover:text-yellow-300" rowSpan={2} onClick={() => handleSort('outlet')}>Outlet <SortIcon col="outlet" /></th>
                  <th className="px-2 py-2.5 text-center font-semibold border-l border-slate-600 text-sky-300 sticky top-0 z-20 bg-slate-800" rowSpan={2}>Today</th>
                  <th className="px-2 py-2.5 text-center font-semibold border-l border-slate-600 text-emerald-300 sticky top-0 z-20 bg-slate-800" colSpan={6}>Attendance</th>
                  <th className="px-2 py-2.5 text-center font-semibold border-l border-slate-600 text-amber-300 sticky top-0 z-20 bg-slate-800" colSpan={4}>Leave & Absent</th>
                  <th className="px-2 py-2.5 text-center font-semibold border-l border-slate-600 text-cyan-300 sticky top-0 z-20 bg-slate-800 cursor-pointer select-none hover:text-yellow-300" rowSpan={2} onClick={() => handleSort('pl_remaining')}>PL Balance <SortIcon col="pl_remaining" /></th>
                  <th className="px-2 py-2.5 text-center font-semibold border-l border-slate-600 text-rose-300 sticky top-0 z-20 bg-slate-800" rowSpan={2}>Deduct Days</th>
                  <th className="px-2 py-2.5 text-center font-semibold border-l border-slate-600 text-emerald-300 sticky top-0 z-20 bg-slate-800 cursor-pointer select-none hover:text-yellow-300" rowSpan={2} onClick={() => handleSort('salary_days')}>Salary Days <SortIcon col="salary_days" /></th>
                </tr>
                <tr className="bg-slate-700 text-slate-200 sticky top-[37px] z-20">
                  <th className={`${thCls} border-l border-slate-600`} onClick={() => handleSort('reg_p')}>Present <SortIcon col="reg_p" /></th>
                  <th className={`${thCls} text-blue-300`} onClick={() => handleSort('weekly_off')}>W.Off <SortIcon col="weekly_off" /></th>
                  <th className={`${thCls} text-indigo-300`} onClick={() => handleSort('ot_hours')}>OT Hrs <SortIcon col="ot_hours" /></th>
                  <th className={`${thCls} text-purple-300`} onClick={() => handleSort('odw')}>ODW <SortIcon col="odw" /></th>
                  <th className={thCls} onClick={() => handleSort('half_days')}>Half <SortIcon col="half_days" /></th>
                  <th className={`${thCls} text-orange-300`} onClick={() => handleSort('missed_punch')}>MP <SortIcon col="missed_punch" /></th>
                  <th className={`${thCls} border-l border-slate-600 text-amber-300`} onClick={() => handleSort('leaves')}>Leaves <SortIcon col="leaves" /></th>
                  <th className={`${thCls} text-red-300`} onClick={() => handleSort('absent')}>Absent <SortIcon col="absent" /></th>
                  <th className={thCls} onClick={() => handleSort('od')}>OD <SortIcon col="od" /></th>
                  <th className={thCls} onClick={() => handleSort('late_minutes')}>Late Min <SortIcon col="late_minutes" /></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((r, idx) => {
                  const salaryDays = calcSalaryDays(r);
                  const plDisplay = formatPLBal(r);
                  const deductDisplay = formatDeductDays(r);
                  const mpCount = missedPunchMap[r.employee_code] || 0;
                  const today = todayStatus[r.employee_code];
                  let todayBadge = d;
                  if (today) {
                    if (today.punch_in && today.punch_out) {
                      todayBadge = <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">Present</span>;
                    } else if (today.punch_in) {
                      todayBadge = <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-300 animate-pulse">In</span>;
                    }
                  }
                  const ec = r.employee_code;
                  const editCell = (field, color) => {
                    const val = edits[ec]?.[field] ?? r[field] ?? 0;
                    return editMode ? (
                      <input type="number" step="0.5" min="0" value={val || ''}
                        onChange={e => updateEdit(ec, field, e.target.value)}
                        className={`w-14 text-center text-xs py-0.5 border rounded font-mono ${color ? `border-${color}-300 bg-${color}-50` : 'border-slate-300'}`} />
                    ) : null;
                  };
                  const showVal = (field, color, fmtFn) => {
                    const v = getVal(r, field);
                    const isEdited = r._mps_edited || (edits[ec] && edits[ec][field] !== undefined);
                    return v > 0 ? <span className={isEdited ? 'underline decoration-dotted' : ''}>{fmtFn ? fmtFn(v) : v}</span> : d;
                  };
                  return (
                    <React.Fragment key={idx}>
                    <tr className={`hover:bg-slate-50 ${r._mps_edited ? 'bg-indigo-50/30' : ''}`} data-testid={`sp-row-${idx}`}>
                      <td className="px-2 py-2.5 text-center text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-2 py-2.5 text-center font-mono text-slate-600">{r.employee_code}</td>
                      <td className="px-3 py-2.5 text-left whitespace-nowrap sticky left-0 bg-white z-10 group-hover:bg-slate-50" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)' }}>
                        {isHR ? (
                          <span className="font-semibold text-indigo-700 cursor-pointer hover:underline" onClick={() => toggleExpand(r.employee_code, r.name)} data-testid={`expand-${r.employee_code}`}>
                            {expandedEmp === r.employee_code ? '▼' : '▶'} {r.name}
                          </span>
                        ) : (
                          <span className="font-semibold text-slate-900">{r.name}</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-center"><span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{r.outlet}</span></td>
                      <td className="px-2 py-2.5 text-center border-l border-slate-100">{todayBadge}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold border-l border-slate-100">{editMode ? editCell('reg_p') : fmt(calcPresent(r))}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-blue-600">{editMode ? editCell('weekly_off', 'blue') : ((r.weekly_off || 0) > 0 ? r.weekly_off : <span className="text-blue-400">0</span>)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-indigo-600">{editMode ? editCell('ot_hours', 'indigo') : (r.ot_hours > 0 ? fmt(r.ot_hours) : d)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-purple-600">{editMode ? editCell('odw', 'purple') : (r.odw > 0 ? r.odw : d)}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{editMode ? editCell('half_days') : (r.half_days > 0 ? r.half_days : d)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-orange-600 font-semibold">{mpCount > 0 ? mpCount : d}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-amber-600 border-l border-slate-100">{editMode ? editCell('leaves', 'amber') : (r.leaves > 0 ? r.leaves : d)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-red-600 font-semibold">{editMode ? editCell('absent', 'red') : (r.absent > 0 ? r.absent : d)}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{r.od > 0 ? r.od : d}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{r.late_minutes > 0 ? r.late_minutes : d}</td>
                      <td className="px-2 py-2.5 text-center font-mono text-cyan-700 border-l border-slate-100 whitespace-nowrap">{plDisplay}</td>
                      <td className="px-2 py-2.5 text-center font-mono border-l border-slate-100">
                        {deductDisplay === 'PL Adjusted'
                          ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">PL Adjusted</span>
                          : deductDisplay
                          ? <span className="text-rose-600 font-semibold">{deductDisplay}</span>
                          : d}
                      </td>
                      <td className="px-2 py-2.5 text-right font-mono font-bold text-emerald-700 border-l border-slate-100">{fmt(salaryDays)}</td>
                    </tr>
                    {expandedEmp === r.employee_code && (
                      <tr>
                        <td colSpan="20" className="p-0">
                          <div className="bg-indigo-50/50 border-t border-b border-indigo-200 px-6 py-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-bold text-indigo-800">Daily Attendance — {r.name} <span className="text-[10px] font-normal text-slate-500 ml-2">(ShiftPro + MPS edits)</span></span>
                              <div className="flex items-center gap-2">
                                {Object.keys(dailyEdits).length > 0 && (
                                  <span className="text-[10px] text-amber-600 font-medium">{Object.keys(dailyEdits).length} day(s) changed</span>
                                )}
                                <Button onClick={saveDailyEdits} disabled={saving || Object.keys(dailyEdits).length === 0} size="sm"
                                  className={`h-7 text-xs ${Object.keys(dailyEdits).length > 0 ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
                                  data-testid="save-daily-btn">
                                  <Save className="h-3 w-3 mr-1" /> {saving ? 'Saving...' : 'Save'}
                                </Button>
                              </div>
                            </div>
                            {dailyLoading ? (
                              <div className="flex justify-center py-4"><div className="animate-spin h-5 w-5 border-b-2 border-indigo-600 rounded-full"></div></div>
                            ) : dailyData.length === 0 ? (
                              <div className="text-center py-3 text-slate-500 text-xs">No daily records found</div>
                            ) : (
                              <div className="overflow-auto max-h-[300px] rounded-lg border border-slate-200">
                                <table className="w-full text-xs bg-white">
                                  <thead className="bg-slate-700 text-white sticky top-0 z-10">
                                    <tr>
                                      <th className="px-2 py-1.5 text-left font-medium">Date</th>
                                      <th className="px-2 py-1.5 text-left font-medium">Day</th>
                                      <th className="px-2 py-1.5 text-center font-medium">Status</th>
                                      <th className="px-2 py-1.5 text-center font-medium">Shift</th>
                                      <th className="px-2 py-1.5 text-center font-medium">Punch In</th>
                                      <th className="px-2 py-1.5 text-center font-medium">Punch Out</th>
                                      <th className="px-2 py-1.5 text-right font-medium">Worked</th>
                                      <th className="px-2 py-1.5 text-right font-medium">OT Hrs</th>
                                      <th className="px-2 py-1.5 text-right font-medium">Late Min</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {dailyData.map((day, di) => {
                                      const dt = new Date(day.date + 'T00:00:00');
                                      const dayName = dt.toLocaleDateString('en-IN', { weekday: 'short' });
                                      const dayNum = day.date?.split('-')[2];
                                      const st = (dailyEdits[day.date]?.status || day.status || '').toLowerCase();
                                      const isWO = st === 'weekly_off' || day.shift_type === 'WO' || day.shift_type === 'OFF';
                                      const isNoData = st === 'no_data';
                                      const isODW = st === 'odw';
                                      const pIn = day.punch_in_ts ? new Date(day.punch_in_ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '–';
                                      const pOut = day.punch_out_ts ? new Date(day.punch_out_ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '–';
                                      const worked = day.worked_minutes ? `${Math.floor(day.worked_minutes / 60)}h ${day.worked_minutes % 60}m` : '–';
                                      const stColor = st === 'holiday' ? 'bg-rose-100 text-rose-700' : st === 'present' ? 'bg-emerald-100 text-emerald-700' : st === 'absent' ? 'bg-red-100 text-red-700' : isWO ? 'bg-blue-100 text-blue-700' : st === 'leave' ? 'bg-amber-100 text-amber-700' : st === 'half_day' ? 'bg-orange-100 text-orange-700' : isODW ? 'bg-purple-100 text-purple-700' : isNoData ? 'bg-gray-100 text-gray-400' : 'bg-slate-100 text-slate-600';
                                      const edited = dailyEdits[day.date] || {};
                                      // Show shift from roster or attendance, with edit corrections
                                      const rosterShift = day.roster_shift_time || day.roster_shift_type || '';
                                      const editedSt = edited.status || '';
                                      const displayShift = editedSt ? (
                                        editedSt === 'weekly_off' ? 'WO' :
                                        editedSt === 'odw' ? 'ODW' :
                                        editedSt === 'absent' ? 'ABSENT' :
                                        editedSt === 'present' ? (rosterShift || day.shift_type || 'REG') :
                                        (rosterShift || day.shift_type || '–')
                                      ) : (
                                        day._holiday ? day.shift_type :
                                        isODW ? (rosterShift ? `ODW` : 'ODW') :
                                        isWO ? 'WO' :
                                        rosterShift || day.shift_type || '–'
                                      );
                                      const rowBg = day._holiday ? 'bg-rose-50/50' : isODW ? 'bg-purple-50/50' : isWO ? 'bg-blue-50/30' : day._mps_edited ? 'bg-yellow-50/40' : '';
                                      return (
                                        <tr key={di} className={`hover:bg-slate-50 ${rowBg}`}>
                                          <td className="px-2 py-1.5 font-mono font-semibold text-slate-700">{dayNum}</td>
                                          <td className="px-2 py-1.5 text-slate-500">{dayName}</td>
                                          <td className="px-2 py-1.5 text-center">
                                            {isHR ? (
                                              <select value={edited.status || day.status || ''} onChange={e => updateDailyEdit(day.date, 'status', e.target.value)}
                                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border-0 cursor-pointer ${stColor}`}>
                                                <option value="present">Present</option>
                                                <option value="absent">Absent</option>
                                                <option value="half_day">Half Day</option>
                                                <option value="leave">Leave</option>
                                                <option value="weekly_off">W.Off</option>
                                                <option value="odw">ODW</option>
                                                <option value="holiday">Holiday</option>
                                                <option value="no_data">No Data</option>
                                              </select>
                                            ) : (
                                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${stColor}`}>{isODW ? 'ODW' : isWO ? 'W.Off' : st === 'holiday' ? 'Holiday' : st === 'no_data' ? 'No Data' : st}</span>
                                            )}
                                          </td>
                                          <td className="px-2 py-1.5 text-center text-[10px] text-slate-500">{displayShift}</td>
                                          <td className="px-2 py-1.5 text-center font-mono text-sky-600">{pIn}</td>
                                          <td className="px-2 py-1.5 text-center font-mono text-emerald-600">{pOut}</td>
                                          <td className="px-2 py-1.5 text-right font-mono">{worked}</td>
                                          <td className="px-2 py-1.5 text-right">
                                            {isHR ? (
                                              <input type="number" step="0.5" min="0" value={edited.ot_hours ?? day.ot_hours ?? ''} onChange={e => updateDailyEdit(day.date, 'ot_hours', e.target.value)}
                                                className="w-12 text-center text-xs py-0.5 border border-slate-300 rounded font-mono" />
                                            ) : (
                                              <span className="font-mono">{day.ot_hours > 0 ? fmt(day.ot_hours) : '–'}</span>
                                            )}
                                          </td>
                                          <td className="px-2 py-1.5 text-right">
                                            {isHR ? (
                                              <input type="number" min="0" value={edited.late_minutes ?? day.late_minutes ?? ''} onChange={e => updateDailyEdit(day.date, 'late_minutes', e.target.value)}
                                                className="w-12 text-center text-xs py-0.5 border border-slate-300 rounded font-mono" />
                                            ) : (
                                              <span className="font-mono">{day.late_minutes > 0 ? day.late_minutes : '–'}</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-20">
                <tr className="bg-slate-100 font-semibold text-xs border-t-2 border-slate-300">
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-3 py-3 text-left sticky left-0 bg-slate-100 z-10" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)' }}>TOTAL ({sorted.length})</td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3 text-center border-l border-slate-200">
                    <span className="text-[10px] text-sky-600">{Object.values(todayStatus).filter(t => t.punch_in && !t.punch_out).length} In</span>
                    <span className="text-[10px] text-emerald-600 ml-1">{Object.values(todayStatus).filter(t => t.punch_in && t.punch_out).length} P</span>
                  </td>
                  <td className="px-2 py-3 text-right font-mono border-l border-slate-200">{fmt(totals.reg_p)}</td>
                  <td className="px-2 py-3 text-right font-mono text-blue-600">{totals.weekly_off}</td>
                  <td className="px-2 py-3 text-right font-mono text-indigo-600">{fmt(totals.ot_hours)}</td>
                  <td className="px-2 py-3 text-right font-mono text-purple-600">{totals.odw}</td>
                  <td className="px-2 py-3 text-right font-mono">{totals.half_days}</td>
                  <td className="px-2 py-3 text-right font-mono text-orange-600">{totals.missed_punch}</td>
                  <td className="px-2 py-3 text-right font-mono text-amber-600 border-l border-slate-200">{totals.leaves}</td>
                  <td className="px-2 py-3 text-right font-mono text-red-600">{totals.absent}</td>
                  <td className="px-2 py-3 text-right font-mono">{totals.od}</td>
                  <td className="px-2 py-3 text-right font-mono">{totals.late_minutes}</td>
                  <td className="px-2 py-3 text-center font-mono text-cyan-700 border-l border-slate-200">{fmt(totals.pl_remaining)}</td>
                  <td className="px-2 py-3 border-l border-slate-200"></td>
                  <td className="px-2 py-3 text-right font-mono font-bold text-emerald-700 border-l border-slate-200">{fmt(totals.salary_days)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftProAttendance;
